import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';

const COMPETENCY_PRESETS = [
  'Test ingresso',
  'Verifica intermedia',
  'Project work',
  'Prova finale',
  'Valutazione orale',
  'Soft skills',
];

export function NewAssessmentModal({ onClose, courseId }: { onClose: () => void; courseId: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    candidateId: '',
    competency: 'Test ingresso',
    score: 70,
  });

  const { data: course } = useQuery({
    queryKey: ['course', courseId],
    queryFn: () => api.get<{ enrollments: Array<{ id: string; candidate: { id: string; firstName: string; lastName: string } }> }>(`/courses/${courseId}`),
  });

  const create = useMutation({
    mutationFn: () => api.post(`/courses/${courseId}/assessments`, {
      candidateId: form.candidateId,
      competency: form.competency,
      score: form.score,
      grade: form.score >= 90 ? 'A' : form.score >= 75 ? 'B' : form.score >= 60 ? 'C' : form.score >= 50 ? 'D' : 'NV',
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['course-assessments', courseId] });
      toast.ok('Valutazione registrata', `${form.competency} · ${form.score}/100`);
      onClose();
    },
    onError: (err) => toast.alert('Errore salvataggio valutazione', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const enrollments = course?.enrollments ?? [];
  const grade = form.score >= 90 ? 'A' : form.score >= 75 ? 'B' : form.score >= 60 ? 'C' : form.score >= 50 ? 'D' : 'NV';
  const gradeTone = form.score >= 75 ? 'text-ok' : form.score >= 60 ? 'text-warn' : 'text-alert';

  return (
    <Modal
      title="Registra valutazione"
      kicker="Corso · Valutazione"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!form.candidateId || create.isPending}>
            Salva <Icon name="check" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Discente" required span={12}>
          <select className="inp" value={form.candidateId} onChange={(e) => setForm({ ...form, candidateId: e.target.value })}>
            <option value="">— seleziona —</option>
            {enrollments.map((en) => (
              <option key={en.candidate.id} value={en.candidate.id}>{en.candidate.firstName} {en.candidate.lastName}</option>
            ))}
          </select>
        </Field>
        <Field label="Prova / Competenza" required span={12}>
          <input list="comp-presets" className="inp" value={form.competency} onChange={(e) => setForm({ ...form, competency: e.target.value })} />
          <datalist id="comp-presets">
            {COMPETENCY_PRESETS.map((c) => <option key={c} value={c} />)}
          </datalist>
        </Field>
        <Field label="Punteggio (0–100)" required span={6}>
          <input className="inp mono text-right" type="number" min={0} max={100} value={form.score} onChange={(e) => setForm({ ...form, score: Number(e.target.value) })} />
        </Field>
        <Field label="Voto" span={6}>
          <div className={`text-[28px] font-light font-mono ${gradeTone}`}>{grade}</div>
        </Field>
      </div>
    </Modal>
  );
}
